import { AxiosError, AxiosResponse } from 'axios';
import { executeHttpRequest } from '../epics/api';
import { TTimesheetApprovalResponse } from '../types/timesheet';
import { TApiResponse } from '../types/api';
import { TDispatch } from '../reducers';

export const FETCH_TIMESHEETS_FOR_APPROVAL = 'FETCH_TIMESHEETS_FOR_APPROVAL';
export const TIMESHEET_APPROVAL_ERROR = 'TIMESHEET_APPROVAL_ERROR';

/**
 * Helper function to handle approval/rejection API responses
 * Checks for success=false in meta and calls appropriate callbacks
 */
function handleApprovalResponse(
    response: AxiosResponse<TApiResponse<any>>,
    onSuccess?: () => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>, message?: string) => void,
): any {
    const responseData = response.data.content;
    const success = response.data.meta?.success;

    // Check if the backend indicates success=false
    if (success === false) {
        const errorMessage = response.data.meta?.detail;
        if (onError) {
            onError(null as any, errorMessage);
        }
        return responseData;
    }

    if (onSuccess) {
        onSuccess();
    }

    return responseData;
}

/**
 * Fetch timesheets for approval
 * @param token - Optional approval token. If not provided, uses session auth.
 */
export function fetchTimesheetsForApprovalAsync(
    token?: string,
    onSuccess?: (data: TTimesheetApprovalResponse) => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>) => void,
) {
    return async(dispatch: TDispatch) => {
        try {
            const url = token 
                ? `timesheets/approval/?token=${token}`
                : 'timesheets/approval/';
            
            const response = await executeHttpRequest(url);

            const data: TTimesheetApprovalResponse = response.data.content;

            dispatch({
                type: FETCH_TIMESHEETS_FOR_APPROVAL,
                payload: data,
            });

            if (onSuccess) {
                onSuccess(data);
            }

            return data;
        } catch (error) {
            const axiosError = error as AxiosError<TApiResponse<unknown>>;

            dispatch({
                type: TIMESHEET_APPROVAL_ERROR,
                payload: axiosError.response?.data || { message: 'Failed to fetch timesheets' },
            });

            if (onError) {
                onError(axiosError);
            }

            throw error;
        }
    };
}

/**
 * Approve multiple timesheets
 * @param timesheetIds - Array of timesheet IDs to approve
 * @param token - Optional approval token. If not provided, uses session auth.
 */
export function approveTimesheetsAsync(
    timesheetIds: number[],
    onSuccess?: () => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>, message?: string) => void,
    token?: string,
) {
    return async() => {
        try {
            const data = {
                ...(token && { token }),
                timesheet_ids: timesheetIds,
            };

            const response = await executeHttpRequest(
                'timesheets/approval/approve/',
                'post',
                data,
            );

            return handleApprovalResponse(response, onSuccess, onError);
        } catch (error) {
            if (onError) {
                onError(error as AxiosError<TApiResponse<unknown>>);
            }

            throw error;
        }
    };
}

/**
 * Reject a single timesheet with reason
 * @param timesheetId - ID of the timesheet to reject
 * @param reason - Rejection reason
 * @param token - Optional approval token. If not provided, uses session auth.
 */
export function rejectTimesheetAsync(
    timesheetId: number,
    reason: string,
    onSuccess?: () => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>, message?: string) => void,
    token?: string,
) {
    return async() => {
        try {
            const data = {
                ...(token && { token }),
                timesheet_id: timesheetId,
                reason,
            };

            const response = await executeHttpRequest(
                'timesheets/approval/reject/',
                'post',
                data,
            );

            return handleApprovalResponse(response, onSuccess, onError);
        } catch (error) {
            if (onError) {
                onError(error as AxiosError<TApiResponse<unknown>>);
            }

            throw error;
        }
    };
}
